import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from '@mui/material';

import {
	ItineraryView,
	PassengerBookingView,
	TripTypeEnum,
} from '@mopla/data-models';
import {
	checkTimeMightChange,
	getItineraryTransportTypesOrdered,
	isMixedItinerary,
} from '@mopla/utils';

import { CrossCircleIcon } from '../../../icons/CrossCircle';
import { InformationIcon } from '../../../icons/Information';
import { IconMessage } from '../../../molecules/IconMessage/IconMessage';
import { TicketDetailsHeader } from '../../../molecules/TicketDetailsHeader/TicketDetailsHeader';
import { BookingStatusBadge } from '../../BookingStatusBadge/BookingStatusBadge';
import {
	IItineraryDateTimeInfoProps,
	ItineraryDateTimeInfo,
} from '../../ItineraryDateTimeInfo/ItineraryDateTimeInfo';

export interface IRouteDetailsHeaderProps {
	route: ItineraryView;
	bookedItinerary?: PassengerBookingView;
	isCancelled?: boolean;
	messages?: ReactNode;
}

const TIME_COLORS: IItineraryDateTimeInfoProps['timeDisplayOverride'] = {
	cancelled: { variant: 'error', icon: <CrossCircleIcon /> },
};

export const RouteDetailsHeader: FC<IRouteDetailsHeaderProps> = ({
	route,
	bookedItinerary,
	isCancelled,
	messages,
}) => {
	const { t } = useTranslation([
		'searchResults',
		'routeDetails',
		'tickets',
		'booking',
	]);

	const transport = getItineraryTransportTypesOrdered(route, [
		TripTypeEnum.TRANSFER_WALK,
	]);

	const timeMightChange = checkTimeMightChange(route);
	const isMixedRoute = isMixedItinerary(route);

	const getMainTitle = () => {
		if (!timeMightChange || isCancelled) {
			return t('common:text.travel_time');
		}

		return t('common:text.estimated_timewindow');
	};

	return (
		<TicketDetailsHeader
			title={getMainTitle()}
			isCancelled={isCancelled}
			transport={transport}
			itineraryDateTime={
				<ItineraryDateTimeInfo
					route={route}
					isCancelled={isCancelled}
					timeTestId="route-duration-timeInterval-block"
					overwrittenTimeTestId="route-duration-initialTimeInterval-block"
					timeDisplayOverride={TIME_COLORS}
				/>
			}
			messages={
				!isCancelled && (
					<>
						{timeMightChange && (
							<IconMessage
								icon={
									<SvgIcon fontSize="small">
										<InformationIcon />
									</SvgIcon>
								}
								message={t('common:text.attentionTimeslot')}
								color="neutral.80"
								variant="caption"
							/>
						)}
						{isMixedRoute && (
							<IconMessage
								icon={
									<SvgIcon fontSize="small">
										<InformationIcon />
									</SvgIcon>
								}
								message={t('common:text.attentionMixedRoutes')}
								color="neutral.80"
								variant="caption"
							/>
						)}
						{messages}
					</>
				)
			}
			badge={
				bookedItinerary && (
					<BookingStatusBadge itinerary={bookedItinerary.itinerary} />
				)
			}
		/>
	);
};
